"use client";

import {
  createContext,
  useCallback,
  useContext,
  useState,
  type ReactNode,
} from "react";
import { useTranslations } from "next-intl";
import type { CartDTO } from "@wmart/shared-types";
import { clientApi, ApiError } from "@/lib/api-client";
import { CartDrawer } from "./CartDrawer";

interface CartState {
  cart: CartDTO | null;
  /** Number of distinct lines in the cart (header badge value) */
  lineCount: number;
  addItem: (input: { variantId: string; quantity: number }) => Promise<void>;
  updateQuantity: (lineId: string, quantity: number) => Promise<void>;
  removeItem: (lineId: string) => Promise<void>;
  /**
   * Re-fetch the cart from the server and update local state. Used to
   * reconcile after external mutations the client wasn't the cause of —
   * primarily the Stripe success redirect, where the webhook clears the
   * cart server-side AFTER `confirmPayment` redirects the buyer (P09-T20
   * Bug #29). Without this, the header badge would render the pre-payment
   * cart count until next full navigation.
   */
  refresh: () => Promise<void>;
  /** Most recent error from a mutation; clear by re-attempting. */
  lastError: string | null;
  /** Whether the slide-in cart drawer is showing. */
  isCartOpen: boolean;
  /** Open the slide-in cart drawer (also fired automatically on a successful add). */
  openCart: () => void;
  /** Close the slide-in cart drawer. */
  closeCart: () => void;
}

const CartContext = createContext<CartState | null>(null);

export function CartProvider({
  children,
  initialCart,
}: {
  children: ReactNode;
  initialCart: CartDTO | null;
}) {
  const t = useTranslations("Cart");
  const [cart, setCart] = useState<CartDTO | null>(initialCart);
  const [lastError, setLastError] = useState<string | null>(null);
  const [isCartOpen, setCartOpen] = useState(false);

  const openCart = useCallback(() => setCartOpen(true), []);
  const closeCart = useCallback(() => setCartOpen(false), []);

  const addItem = useCallback(
    async (input: { variantId: string; quantity: number }) => {
      setLastError(null);
      try {
        const next = await clientApi.post<CartDTO>(
          "/v1/storefront/cart/items",
          input,
        );
        setCart(next);
        // Slide the drawer in on a successful add — the single add-to-cart
        // chokepoint, so every caller (tile, PDP, buy-again, reorder, saved
        // list) gets the confirmation UI for free.
        setCartOpen(true);
      } catch (err) {
        setLastError(formatError(err, t));
        throw err;
      }
    },
    [t],
  );

  const updateQuantity = useCallback(
    async (lineId: string, quantity: number) => {
      setLastError(null);
      try {
        const next = await clientApi.patch<CartDTO>(
          `/v1/storefront/cart/items/${lineId}`,
          { quantity },
        );
        setCart(next);
      } catch (err) {
        setLastError(formatError(err, t));
        throw err;
      }
    },
    [t],
  );

  const removeItem = useCallback(
    async (lineId: string) => {
      setLastError(null);
      try {
        const next = await clientApi.del<CartDTO>(
          `/v1/storefront/cart/items/${lineId}`,
        );
        setCart(next);
      } catch (err) {
        setLastError(formatError(err, t));
        throw err;
      }
    },
    [t],
  );

  // P09-T20 Bug #29 — refresh is intentionally tolerant of 401/403/404: a
  // signed-out or no-cart-yet buyer has nothing to reconcile, so silently
  // setting null matches the SSR layout's behaviour for those states.
  const refresh = useCallback(async () => {
    try {
      const next = await clientApi.get<CartDTO>("/v1/storefront/cart");
      setCart(next);
    } catch (err) {
      if (err instanceof ApiError && (err.status === 401 || err.status === 403 || err.status === 404)) {
        setCart(null);
        return;
      }
      // Any other error: keep current state, surface for diagnostics.
      setLastError(formatError(err, t));
    }
  }, [t]);

  const lineCount = cart?.lines.length ?? 0;

  return (
    <CartContext.Provider
      value={{
        cart,
        lineCount,
        addItem,
        updateQuantity,
        removeItem,
        refresh,
        lastError,
        isCartOpen,
        openCart,
        closeCart,
      }}
    >
      {children}
      <CartDrawer isOpen={isCartOpen} onClose={closeCart} />
    </CartContext.Provider>
  );
}

export function useCart(): CartState {
  const ctx = useContext(CartContext);
  if (!ctx) {
    throw new Error("useCart must be used inside <CartProvider>");
  }
  return ctx;
}

function formatError(
  err: unknown,
  t: (key: string) => string,
): string {
  if (err instanceof ApiError) {
    if (err.status === 401) return t("errorSignIn");
    if (err.status === 403) return t("errorAwaitingApproval");
    // 410 Gone — soft-deleted variant in an existing cart line (catalog admin
    // archived it). T6's mapPricingError maps the facade's
    // "PricingFacade.priceCart: variant soft-deleted: <id>" error to 410. The
    // 422 branch below is for "variant unknown" — defensive (FK-violating).
    if (err.status === 410) return t("errorNoLongerSold");
    if (err.status === 422) return t("errorNoLongerAvailable");
    return err.problem?.detail ?? t("errorGeneric");
  }
  return err instanceof Error ? err.message : t("errorGeneric");
}
